import React, { useEffect, useRef, useState } from "react";
import useRevealSequence from "../useRevealSequence";
import Celebration from "../Celebration";

/**
 * Royal Orbit - the board turns as one.
 *
 * The yantras ride an ellipse and the whole ring rotates. When the round
 * ends the ring eases to a stop with the winner at the top, and then the
 * winner alone is pulled into the middle and crowned.
 */

const RX = 300; // ellipse half-width, in the 820 stage
const RY = 250;
const SPIN_SPEED = 150; // degrees a second while it waits
const LANDING_MS = 900;

const easeOutQuart = (p) => 1 - Math.pow(1 - p, 4);

export default function RoyalOrbit({ tiles, winnerIndex, onFinished }) {
  const [angle, setAngle] = useState(0);
  const [hot, setHot] = useState(-1);
  const [pulled, setPulled] = useState(-1);
  const [landed, setLanded] = useState(-1);
  const angleRef = useRef(0);
  const rafRef = useRef(null);
  // The free spin and the landing both drive the ring's angle, so the
  // free spin is told to stop before the landing starts; otherwise it
  // goes on nudging the ring after it has come to rest.
  const freeSpinRef = useRef(true);
  const count = tiles.length;
  const step = 360 / Math.max(1, count);

  // Free rotation until the sequence takes over.
  useEffect(() => {
    let last = performance.now();
    const loop = (now) => {
      if (!freeSpinRef.current) return;
      const dt = Math.min(64, now - last);
      last = now;
      angleRef.current = (angleRef.current + (dt / 1000) * SPIN_SPEED) % 360;
      setAngle(angleRef.current);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useRevealSequence(
    async (ctx) => {
      const target = await ctx.waitForWinner();

      // Ease to a stop with the winner at twelve o'clock, five turns on.
      freeSpinRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const from = angleRef.current;
      const resting = (360 - target * step) % 360;
      const total = 360 + ((resting - from) % 360 + 360) % 360;
      const started = performance.now();

      // A browser stops calling requestAnimationFrame when its tab or
      // window is not in front, so a landing driven by frames alone hangs
      // there for ever - and a show that never ends never takes itself off
      // the board. The wait below is raced against a plain timer, and the
      // finished state is set either way.
      const landing = new Promise((resolve) => {
        const frame = (now) => {
          const p = Math.min(1, (now - started) / LANDING_MS);
          angleRef.current = from + total * easeOutQuart(p);
          setAngle(angleRef.current);
          if (p < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
      await Promise.race([landing, ctx.sleep(LANDING_MS + 400)]);
      angleRef.current = from + total;
      setAngle(angleRef.current);

      setHot(target);
      await ctx.sleep(120);
      setPulled(target);
      await ctx.sleep(300);
      setLanded(target);
      await ctx.sleep(1200);
      onFinished();
    },
    winnerIndex,
    [count],
  );

  const winner = landed >= 0 ? tiles[landed] : null;

  return (
    <div className="rv-orbit">
      {tiles.map((tile, i) => {
        const a = ((i * step - 90 + angle) * Math.PI) / 180;
        const isPulled = i === pulled;
        return (
          <div
            key={tile.id}
            className={[
              "rv-card rv-card--float",
              i === hot ? "rv-card--active" : "",
              isPulled ? "rv-card--pulled" : "",
              pulled >= 0 && !isPulled ? "rv-card--fade" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              left: isPulled ? "50%" : `${410 + Math.cos(a) * RX}px`,
              top: isPulled ? "50%" : `${380 + Math.sin(a) * RY}px`,
            }}
          >
            <img src={tile.img} alt={tile.name || ""} />
            <span>{tile.name}</span>
          </div>
        );
      })}

      {winner ? <Celebration tile={winner} seed={landed} compact /> : null}
      {winner ? <div className="rv-centre-name">{winner.name}</div> : null}
    </div>
  );
}
